<script setup>
/**
 * Renders a `media_list` block — the latest published press articles, resolved
 * server-side into `data.items` (MediaArticleSummary shape). Count-aware grid,
 * mirroring the media-coverage section. The media analog of EventListBlock,
 * including the date sorter on full listings (no `limit` — the /media page).
 */
import MediaArticleBlock from "~/components/blocks/MediaArticleBlock.vue";
import ListSortSelect from "~/components/ListSortSelect.vue";
import { sortByDate } from "~/utils/dates";

const { t } = useI18n();
const localePath = useLocalePath();

const props = defineProps({ data: { type: Object, required: true } });

const sortMode = ref("default");
const showSorter = computed(
  () => !props.data.limit && (props.data.items || []).length > 1,
);
const items = computed(() =>
  sortByDate(props.data.items, sortMode.value, "date"),
);
</script>

<template>
  <section v-if="(data.items || []).length" class="section-soft">
    <div class="container-x">
      <!-- Optional section heading + "view all" link -->
      <div v-if="data.heading" class="mb-10 md:mb-12 flex flex-wrap items-end justify-between gap-4">
        <div class="space-y-3">
          <p v-if="data.eyebrow" class="text-xs tracking-[0.3em] uppercase text-accent-blue font-semibold">{{ data.eyebrow }}</p>
          <h2 class="font-display text-3xl md:text-4xl">{{ data.heading }}</h2>
          <div class="rainbow-rule"></div>
        </div>
        <NuxtLink
          v-if="data.view_all_href"
          :to="localePath(data.view_all_href)"
          class="no-underline inline-flex items-center gap-1.5 text-sm font-medium text-brand-green hover:text-brand-orange"
        >
          {{ data.view_all_label || t("home.media.viewAll") }}
          <span aria-hidden="true">→</span>
        </NuxtLink>
      </div>

      <!-- Date sorter — full listings only (default = curated portal order) -->
      <div v-if="showSorter" class="mb-6 flex justify-end">
        <ListSortSelect v-model="sortMode" />
      </div>

      <div
        class="grid gap-6"
        :class="items.length === 1 ? 'max-w-md'
              : items.length === 2 ? 'sm:grid-cols-2'
              : 'sm:grid-cols-2 lg:grid-cols-3'"
      >
        <MediaArticleBlock v-for="m in items" :key="m.id ?? m.url" :data="m" />
      </div>
    </div>
  </section>
</template>
